import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Ban, CreditCard, Loader2, Move, Package, Undo2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { localizedName } from '@/components/localized-name';
import { usePermission } from '@/features/auth/auth.store';
import { paymentMethodLabelKey } from '@/features/events/payment-methods';
import { ChangePackageDialog } from '@/features/events/change-package-dialog';
import { MoveBookingDialog } from '@/features/events/move-booking-dialog';
import { RecordPaymentDialog } from '@/features/events/record-payment-dialog';
import { env } from '@/config/env';
import { formatCurrency, formatDateTime, formatPhone } from '@/lib/format';
import { PaymentCategoryCell } from './payment-category';
import {
  useBookingDetail,
  useCancelBooking,
  useVoidPayment,
} from './bookings.api';
import { BOOKING_STATUS, type BookingPayment } from './types';

/** Booking status, as a badge. */
function statusBadge(status: number | null, t: (k: string) => string) {
  if (status === BOOKING_STATUS.confirmed) {
    return <Badge variant="success">{t('manageBookings.confirmed')}</Badge>;
  }
  if (status === BOOKING_STATUS.cancelled) {
    return <Badge variant="destructive">{t('manageBookings.cancelled')}</Badge>;
  }
  return <Badge variant="warning">{t('manageBookings.pending')}</Badge>;
}

/** A payment's outcome, in one badge — voided, refunded, failed, pending or good. */
function paymentBadge(payment: BookingPayment, t: (k: string) => string) {
  if (payment.voidedAt) {
    return <Badge variant="secondary">{t('manageBookings.voided')}</Badge>;
  }
  if ((payment.refundedAmount ?? 0) > 0) {
    return (
      <Badge variant="outline">
        {t('manageBookings.refunded')} {formatCurrency(payment.refundedAmount)}
      </Badge>
    );
  }
  if (payment.failed || payment.isFailed) {
    return <Badge variant="destructive">{t('manageBookings.failed')}</Badge>;
  }
  if (payment.isPending) {
    return <Badge variant="warning">{t('manageBookings.pending')}</Badge>;
  }
  return <Badge variant="success">{t('manageBookings.successful')}</Badge>;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <dt className="text-muted-foreground shrink-0">{label}</dt>
      <dd className="text-right">{children}</dd>
    </div>
  );
}

/**
 * Everything known about one booking: who, what, when, how it was paid, every
 * payment against it, and where it has been moved.
 */
export function BookingDetailSheet({
  bookingId,
  open,
  onOpenChange,
}: {
  bookingId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const canManageMoney = usePermission('isFinance');
  const isAdmin = usePermission('isAdmin');
  const canCancelBookings = usePermission('canCancelBookings');

  const [moving, setMoving] = useState(false);
  const [paying, setPaying] = useState(false);
  const [changingPackage, setChangingPackage] = useState(false);

  const detail = useBookingDetail(open ? bookingId : null);
  const voidPayment = useVoidPayment();
  const cancelBooking = useCancelBooking();

  const booking = detail.data?.booking;
  const canCancel =
    canCancelBookings && booking?.status !== BOOKING_STATUS.cancelled;

  // One flat list across every purchase — an admin thinks about "what was
  // paid", not about which purchase row a payment happens to hang off.
  const payments: BookingPayment[] = (detail.data?.purchases ?? []).flatMap(
    (purchase) =>
      (purchase.payments ?? []).map((payment) => ({
        ...payment,
        purchaseAmount: purchase.amount,
      }))
  );

  const moves = detail.data?.moves ?? [];
  const lastMove = moves.length ? moves[moves.length - 1] : null;
  // Nothing to reschedule about a booking that is not happening.
  const canMove = canManageMoney && booking?.status !== BOOKING_STATUS.cancelled;

  const packageChanges = detail.data?.packageChanges ?? [];
  /*
   * Changing a package re-prices purchases and can settle them — the same
   * class of act as correcting a payment — so the server gates it on the
   * financial roles, a narrower list than the one that manages the packages
   * themselves. Offered only on a booking that is on a package to begin with.
   */
  const canChangePackage =
    isAdmin &&
    Boolean(booking?.packageId) &&
    booking?.status !== BOOKING_STATUS.cancelled;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full gap-0 overflow-y-auto p-0 sm:max-w-xl"
        >
          <SheetHeader className="flex-row flex-wrap items-start justify-between gap-3 border-b pr-12">
            <SheetTitle>
              {booking
                ? [booking.firstName, booking.lastName].filter(Boolean).join(' ')
                : t('manageBookings.booking')}
            </SheetTitle>
            <span className="flex flex-wrap gap-2">
              {canChangePackage && booking && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setChangingPackage(true)}
                >
                  <Package className="size-3.5" />
                  {t('manageBookings.changePackage')}
                </Button>
              )}
              {canMove && booking && (
                <Button variant="outline" size="sm" onClick={() => setMoving(true)}>
                  <Move className="size-3.5" />
                  {t('manageBookings.moveBooking')}
                </Button>
              )}
              {canCancel && booking && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={cancelBooking.isPending}
                    >
                      {cancelBooking.isPending ? (
                        <Loader2 className="size-3.5 animate-spin" />
                      ) : (
                        <Ban className="size-3.5" />
                      )}
                      {t('manageBookings.cancelBooking')}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {t('manageBookings.cancelBooking')}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('manageBookings.cancelBookingBody', {
                          name:
                            [booking.firstName, booking.lastName]
                              .filter(Boolean)
                              .join(' ') || booking.email,
                        })}
                        {env.bookingCancellationFee > 0 &&
                          ` ${t('manageBookings.cancelBookingFee', {
                            fee: formatCurrency(env.bookingCancellationFee),
                          })}`}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>
                        {t('manageBookings.keepBooking')}
                      </AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        onClick={() =>
                          cancelBooking.mutate(
                            {
                              bookingId: booking.id,
                              cancellationFee: env.bookingCancellationFee,
                            },
                            {
                              onSuccess: () =>
                                toast.success(
                                  t('manageBookings.bookingCancelled')
                                ),
                              onError: (error) =>
                                toast.error(
                                  error instanceof Error
                                    ? error.message
                                    : t('manageBookings.actionFailed')
                                ),
                            }
                          )
                        }
                      >
                        {t('manageBookings.cancelBooking')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </span>
          </SheetHeader>

          <div className="space-y-5 p-4">
            {detail.isLoading && (
              <div className="space-y-2">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="flex justify-between gap-4">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-40" />
                  </div>
                ))}
              </div>
            )}

            {(detail.isError || (detail.data && !booking)) && (
              <p className="text-muted-foreground py-10 text-center text-sm">
                {t('manageBookings.detailFailed')}
              </p>
            )}

            {booking && (
              <>
                <dl className="space-y-2">
                  <Row label={t('manageBookings.user')}>
                    <span className="block">{booking.email}</span>
                    <span className="text-muted-foreground block text-xs">
                      {formatPhone(booking.phoneNumber)}
                    </span>
                  </Row>

                  <Row label={t('manageBookings.event')}>
                    <span className="block">{booking.eventName}</span>
                    <span className="text-muted-foreground block text-xs">
                      {booking.locationName}
                    </span>
                    {booking.isFirstTimeAtEvent && (
                      <Badge variant="outline" className="mt-1">
                        {t('manageBookings.firstTime')}
                      </Badge>
                    )}
                  </Row>

                  <Row label={t('manageBookings.when')}>
                    <span className="block">
                      {formatDateTime(booking.timeslotStartTime)}
                    </span>
                    {lastMove && (
                      <span className="text-muted-foreground block text-xs">
                        {t('manageBookings.movedFrom')}{' '}
                        {formatDateTime(lastMove.fromStartTime)}
                      </span>
                    )}
                  </Row>

                  <Row label={t('manageBookings.status')}>
                    <span className="flex flex-wrap justify-end gap-1.5">
                      {statusBadge(booking.status, t)}
                      {booking.checkedInAt ? (
                        <Badge variant="success">
                          {t('manageBookings.checkedIn')}
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          {t('manageBookings.notCheckedIn')}
                        </Badge>
                      )}
                    </span>
                  </Row>

                  {booking.packageId && (
                    <Row label={t('manageBookings.package')}>
                      <span className="block">
                        {localizedName(
                          {
                            nameEn: booking.packageNameEn,
                            nameEs: booking.packageNameEs,
                          },
                          language
                        )}
                      </span>
                    </Row>
                  )}

                  <Row label={t('manageBookings.paidHow')}>
                    <PaymentCategoryCell row={booking} language={language} />
                  </Row>

                  <Row label={t('manageBookings.money')}>
                    <span className="block tabular-nums">
                      {booking.packageId
                        ? t('manageBookings.soldAt')
                        : t('manageBookings.eventPrice')}
                      : {formatCurrency(booking.price)}
                    </span>
                    {(booking.outstanding ?? 0) > 0 && (
                      <span className="text-warning block text-xs tabular-nums">
                        {t('manageBookings.outstanding')}:{' '}
                        {formatCurrency(booking.outstanding)}
                      </span>
                    )}
                    {(booking.refunded ?? 0) > 0 && (
                      <span className="text-muted-foreground block text-xs tabular-nums">
                        {t('manageBookings.refunded')}:{' '}
                        {formatCurrency(booking.refunded)}
                      </span>
                    )}
                  </Row>

                  {booking.userSubscriptionId && (
                    <Row label={t('manageBookings.membershipUsed')}>
                      <span className="block">
                        {language === 'en'
                          ? booking.benefitNameEn
                          : booking.benefitNameEs}
                      </span>
                      <span className="text-muted-foreground block text-xs tabular-nums">
                        {t('manageBookings.benefitValue')}:{' '}
                        {formatCurrency(booking.price)}
                      </span>
                    </Row>
                  )}

                  <Row label={t('manageBookings.bookedAt')}>
                    {formatDateTime(booking.createdAt)}
                  </Row>

                  {booking.comments && (
                    <Row label={t('manageBookings.comments')}>
                      {booking.comments}
                    </Row>
                  )}

                  {(detail.data?.additionalInformation1Response ||
                    detail.data?.additionalInformation2Response) && (
                    <Row label={t('manageBookings.additionalInformation')}>
                      <span className="block">
                        {detail.data.additionalInformation1Response}
                      </span>
                      <span className="block">
                        {detail.data.additionalInformation2Response}
                      </span>
                    </Row>
                  )}
                </dl>

                {packageChanges.length > 0 && (
                  <>
                    <Separator />
                    <section className="space-y-2">
                      <h3 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                        {t('manageBookings.packageHistory')}
                      </h3>
                      <ol className="space-y-2 text-sm">
                        {packageChanges.map((entry, index) => (
                          <li key={index} className="rounded-lg border p-2">
                            <p>
                              {localizedName(
                                {
                                  nameEn: entry.fromPackageNameEn,
                                  nameEs: entry.fromPackageNameEs,
                                },
                                language
                              ) || t('manageBookings.noPackage')}{' '}
                              →{' '}
                              {localizedName(
                                {
                                  nameEn: entry.toPackageNameEn,
                                  nameEs: entry.toPackageNameEs,
                                },
                                language
                              )}
                            </p>
                            {/* Snapshots of what each cost at the time, so they
                                are shown as given rather than re-derived. */}
                            <p className="text-muted-foreground text-xs tabular-nums">
                              {formatCurrency(entry.fromPrice)} →{' '}
                              {formatCurrency(entry.toPrice)}
                            </p>
                            <p className="text-muted-foreground text-xs">
                              {formatDateTime(entry.changedAt)}
                              {entry.changedByName &&
                                ` · ${t('manageBookings.changedBy')} ${entry.changedByName}`}
                            </p>
                            {entry.reason && (
                              <p className="text-muted-foreground text-xs italic">
                                {entry.reason}
                              </p>
                            )}
                          </li>
                        ))}
                      </ol>
                    </section>
                  </>
                )}

                {moves.length > 0 && (
                  <>
                    <Separator />
                    <section className="space-y-2">
                      <h3 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                        {t('manageBookings.moveHistory')}
                      </h3>
                      <ol className="space-y-2 text-sm">
                        {moves.map((move, index) => (
                          <li key={index} className="rounded-lg border p-2">
                            <p>
                              {formatDateTime(move.fromStartTime)} →{' '}
                              {formatDateTime(move.toStartTime)}
                              {move.capacityIncreased && (
                                <Badge variant="outline" className="ml-1.5">
                                  {t('manageBookings.capacityIncreased')}
                                </Badge>
                              )}
                            </p>
                            <p className="text-muted-foreground text-xs">
                              {formatDateTime(move.movedAt)}
                              {move.movedByName &&
                                ` · ${t('manageBookings.movedBy')} ${move.movedByName}`}
                            </p>
                            {move.reason && (
                              <p className="text-muted-foreground text-xs italic">
                                {move.reason}
                              </p>
                            )}
                          </li>
                        ))}
                      </ol>
                    </section>
                  </>
                )}

                <Separator />

                <section className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                      {t('manageBookings.payments')}
                    </h3>
                    {canManageMoney && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPaying(true)}
                      >
                        <CreditCard className="size-3.5" />
                        {t('manageBookings.recordPayment')}
                      </Button>
                    )}
                  </div>

                  {payments.length === 0 && (
                    <p className="text-muted-foreground rounded-lg border border-dashed py-6 text-center text-xs">
                      {t('manageBookings.noPayments')}
                    </p>
                  )}

                  {payments.map((payment) => (
                    <div
                      key={payment.id}
                      className="flex flex-wrap items-start justify-between gap-2 rounded-lg border p-2 text-sm"
                    >
                      <div className="min-w-0">
                        <p className="font-medium tabular-nums">
                          {formatCurrency(payment.amount)}
                          <span className="text-muted-foreground ml-1.5 font-normal">
                            {t(paymentMethodLabelKey(payment.method))}
                          </span>
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {formatDateTime(payment.recordedAt ?? payment.createdAt)}
                          {payment.recordedByName &&
                            ` · ${t('manageBookings.recordedBy')} ${payment.recordedByName}`}
                        </p>
                        {payment.offlineReference && (
                          <p className="text-muted-foreground font-mono text-xs">
                            {payment.offlineReference}
                          </p>
                        )}
                        {payment.adminNotes && (
                          <p className="text-muted-foreground text-xs italic">
                            {payment.adminNotes}
                          </p>
                        )}
                      </div>

                      <div className="flex shrink-0 items-center gap-1.5">
                        {paymentBadge(payment, t)}
                        {canManageMoney &&
                          payment.isOffline &&
                          !payment.voidedAt && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  aria-label={t('manageBookings.voidPayment')}
                                >
                                  <Undo2 className="size-3.5" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    {t('manageBookings.voidPayment')}
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    {t('manageBookings.voidPaymentBody')}
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>
                                    {t('common.cancel')}
                                  </AlertDialogCancel>
                                  <AlertDialogAction
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    onClick={() =>
                                      voidPayment.mutate(payment.id, {
                                        onSuccess: () => {
                                          toast.success(
                                            t('manageBookings.paymentVoided')
                                          );
                                          void detail.refetch();
                                        },
                                        onError: () =>
                                          toast.error(
                                            t('manageBookings.actionFailed')
                                          ),
                                      })
                                    }
                                  >
                                    {t('manageBookings.voidPayment')}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                      </div>
                    </div>
                  ))}
                </section>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {booking && (
        <>
          <MoveBookingDialog
            booking={{ id: booking.id }}
            open={moving}
            onOpenChange={setMoving}
            onMoved={() => void detail.refetch()}
          />

          <ChangePackageDialog
            booking={{
              id: booking.id,
              eventId: booking.eventId,
              packageId: booking.packageId,
            }}
            open={changingPackage}
            onOpenChange={setChangingPackage}
            onChanged={() => void detail.refetch()}
          />

          <RecordPaymentDialog
            targetId={booking.id}
            purchases={detail.data?.purchases ?? []}
            open={paying}
            onOpenChange={setPaying}
            onSaved={() => void detail.refetch()}
          />
        </>
      )}
    </>
  );
}
